import { useMemo } from 'react';

import { useNavigationQuery } from '../../api/portalGateway/navigation';
import { useGlobalFilterValidatedData } from '../../hooks/filter';
import { applyEffectiveGlobalSelections } from '../../utils/applyEffectiveGlobalSelections';

import { PageFilterSelections } from './useCombinedPageFilterSelections';
import { useGlobalSelections } from './useSelections';

/**
 * Returns global filter selections with visible option values injected for
 * multiselect filters that have no explicit user selection ("All").
 *
 * Only activates when at least one global filter already has a selection;
 * otherwise returns raw selections unchanged.
 *
 * Uses the read-only useGlobalFilterValidatedData to avoid duplicate
 * useSanitizeSelections side effects from the authoritative useGlobalFilterQuery.
 *
 * Use this for data queries and downstream filter option queries.
 * Do NOT use for global filter cascading, URL sync, or persistence.
 */
export function useEffectiveGlobalSelections(): PageFilterSelections {
  const { data: nav } = useNavigationQuery();
  const rawSelections = useGlobalSelections();
  const validatedData = useGlobalFilterValidatedData(
    nav?.globalFilters,
    nav?.rootPageId
  );

  return useMemo(
    () =>
      applyEffectiveGlobalSelections(
        rawSelections,
        validatedData,
        nav?.globalFilters
      ),
    [rawSelections, validatedData, nav?.globalFilters]
  );
}
